import Pusher from "pusher-js";
import * as PusherTypes from "pusher-js";
import { useProfileStore } from "@/store/Profile";
import { useScheduleStore } from "@/store/Schedule";
import loggingService from "@/services/LoggingService";
import * as Sentry from "@sentry/vue";
import i18n from "@/locale";

export interface Msg {
  msgId: string;
}

export interface Upgrade {
  url: string | null | undefined;
}

type PusherAckFn = (eventName: string, data: unknown) => void;

function bindGlobalIfAvailable(ch: PusherTypes.Channel, cb: PusherAckFn): void {
  // pusher-js doesn't type bind_global, so we feature-detect it without using `any`
  const maybe = (ch as unknown as { bind_global?: (cb: PusherAckFn) => void }).bind_global;
  if (typeof maybe === "function") maybe(cb);
}

export class PusherServiceClass {
  private pusherInstance: Pusher | undefined;
  private resourceChannel: PusherTypes.Channel | undefined;
  private organizationChannel: PusherTypes.Channel | undefined;

  private retryChannelSubscription(
    channelName: string,
    setChannel: (ch: PusherTypes.Channel) => void,
    bindHandlers: (ch: PusherTypes.Channel) => void
  ) {
    const maxRetries = 5;
    let attempts = 0;
    let current: PusherTypes.Channel | undefined;

    const subscribeWithRetry = () => {
      if (!this.pusherInstance) return;

      if (current) {
        try {
          current.unbind_all();
          this.pusherInstance.unsubscribe(channelName);
        } catch {
          /* ignore */
        }
      }

      const channel = this.pusherInstance.subscribe(channelName);
      current = channel;
      setChannel(channel);

      const onError = (status: number) => {
        loggingService.error("Pusher Retry", `Subscription error (${status}) on ${channelName}`);
        if (attempts < maxRetries) {
          attempts++;
          const delay = 1000 * Math.pow(2, attempts - 1); // 1s, 2s, 4s, 8s, 16s
          loggingService.info(
            "Pusher Retry",
            `Retrying ${channelName} in ${delay}ms (attempt ${attempts}/${maxRetries})`
          );
          window.setTimeout(subscribeWithRetry, delay);
        } else {
          loggingService.error("Pusher Retry", `Max retries reached for ${channelName}`);
          window.location.reload();
        }
      };

      channel.bind("pusher:subscription_error", onError);
      channel.bind("pusher:subscription_succeeded", () => {
        attempts = 0;
        loggingService.info("Pusher Retry", `✅ Successfully subscribed to ${channelName}`);
        bindHandlers(channel);
      });
    };

    subscribeWithRetry();
  }

  initialize(): void {
    const profileStore = useProfileStore();

    if (
      !profileStore.pusher ||
      !profileStore.pusher.appKey ||
      !profileStore.pusher.resourceChannel ||
      !profileStore.pusher.organizationChannel
    ) {
      loggingService.error("Pusher Initialize", "❗️ Failed to receive pusher data");
      return;
    }

    // Snapshot config & optional ack callback (avoid nullable access inside closures)
    const cfg = profileStore.pusher;
    const ackEnabled = !!cfg.ackEnabled; // no explicit :boolean (fixes no-inferrable-types)
    const storeWithAck = profileStore as typeof profileStore & { pusherAck?: PusherAckFn };
    const pusherAck: PusherAckFn | undefined = storeWithAck.pusherAck;

    if (cfg.verboseLogging) {
      Pusher.logToConsole = true;
      Pusher.log = (message: string) => {
        loggingService.debug("Pusher Logger", `🤖 ${message}`);
      };
    }

    this.pusherInstance = new Pusher(cfg.appKey, {
      cluster: "eu",
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
      authEndpoint: `/pusher/auth`,
    });

    loggingService.debug("PusherService.ts", "Initialized connection with Pusher");

    const conn = this.pusherInstance.connection;
    conn.bind("state_change", (s: { previous: string; current: string }) =>
      loggingService.info("Pusher Connection", `state ${s.previous} → ${s.current}`)
    );
    conn.bind("connected", profileStore.connect);
    conn.bind("unavailable", profileStore.disconnect);
    conn.bind("connecting", profileStore.disconnect);
    conn.bind("error", (error: unknown) => {
      let msg: string;
      try {
        msg = typeof error === "string" ? error : JSON.stringify(error);
      } catch {
        msg = String(error);
      }
      loggingService.info("PusherService.ts", `📵 Connection error: ${msg}`);
    });

    const scheduleStore = useScheduleStore();

    const makeResourceBinder = (ackOn: boolean, ackFn?: PusherAckFn) => (ch: PusherTypes.Channel) => {
      ch.bind("update-schedule", () => {
        if (scheduleStore.shouldSkipRealtimeRefresh("pusher:update-schedule")) {
          return;
        }

        void scheduleStore.refreshTablet({ source: "pusher:update-schedule" });
      });
      ch.bind("update-profile", profileStore.getProfile);

      ch.bind("unpair", () => {
        loggingService.info("PusherService.ts", `🔚 Unpairing`);
        window.location.reload();
      });

      ch.bind("dump-sentry", () => {
        Sentry.withScope(scope => {
          scope.setFingerprint([String(new Date())]);
          Sentry.captureMessage("Sentry dump");
        });
      });

      ch.bind("upgrade", (data: Upgrade) => {
        if (!data.url) {
          loggingService.info("PusherService.ts", `♺ Reloading page`);
          window.location.reload();
          return;
        }
        loggingService.info("PusherService.ts", `🆕 Upgrading to: ${data.url}`);
        window.location.replace(data.url);
      });

      if (ackOn && ackFn) {
        bindGlobalIfAvailable(ch, ackFn);
      }
    };

    const makeOrganizationBinder = (ackOn: boolean, ackFn?: PusherAckFn) => (ch: PusherTypes.Channel) => {
      ch.bind("update-settings", async () => {
        const { data } = await profileStore.getProfile();
        const lang = data?.profile.config.language || "en";
        i18n.global.locale = lang;
      });

      if (ackOn && ackFn) {
        bindGlobalIfAvailable(ch, ackFn);
      }
    };

    this.retryChannelSubscription(
      cfg.resourceChannel,
      ch => {
        this.resourceChannel = ch;
      },
      makeResourceBinder(ackEnabled, pusherAck)
    );

    this.retryChannelSubscription(
      cfg.organizationChannel,
      ch => {
        this.organizationChannel = ch;
      },
      makeOrganizationBinder(ackEnabled, pusherAck)
    );

    if (cfg.visibilityChangeHandler && !document.body.dataset.pusherVisibilityHandlerAttached) {
      document.addEventListener("visibilitychange", () => {
        loggingService.info("PusherService.ts", `visibilitychange: document.hidden ${document.hidden}`);
        if (!document.hidden) {
          // TODO: Ideally, we should check Pusher connectivity first and reload only when necessary
          window.location.reload();
        }
      });

      document.addEventListener("freeze", () => {
        loggingService.info("PusherService.ts", `OMG! freeze`);
      });

      document.addEventListener("resume", () => {
        loggingService.info("PusherService.ts", `OMG! resume`);
      });

      document.body.dataset.pusherVisibilityHandlerAttached = "true";
    }
  }
}

const pusherService = new PusherServiceClass();

export default pusherService;
